import React, { useState } from "react";

type ServiceCard = {
  id: string;
  icon: string;
  title: string;
  description: string;
};

type ServiceDetail = {
  category: string;
  title: string;
  description: string;
  benefits: string[];
};

const services: ServiceCard[] = [
  {
    id: "residential",
    icon: "home",
    title: "Residential Cleaning",
    description:
      "Tailored home maintenance programs that prioritize your family's health and comfort. We use eco-friendly agents to ensure a pristine, safe environment.",
  },
  {
    id: "commercial",
    icon: "business",
    title: "Commercial Cleaning",
    description:
      "High-standard maintenance for retail spaces, warehouses, and industrial facilities. Designed to meet strict safety and hygiene regulations.",
  },
  {
    id: "office",
    icon: "desk",
    title: "Office Cleaning",
    description:
      "Productivity begins with a clean workspace. Our team specializes in sanitizing workstations, breakrooms, and corporate boardrooms with precision.",
  },
  {
    id: "deep",
    icon: "sanitizer",
    title: "Deep Cleaning",
    description:
      "Intensive restoration for neglected areas. We reach into the crevices, scrubbing every inch from floorboards to vents for a new build feel.",
  },
  {
    id: "construction",
    icon: "construction",
    title: "Post Construction Cleaning",
    description:
      "Removing the dust, debris, and residues of renovation. We prepare your newly built or renovated space for immediate occupancy and display.",
  },
  {
    id: "pest",
    icon: "bug_report",
    title: "Pest Control",
    description:
      "Comprehensive pest management including termite treatment, rodent control, and general preventative spraying to protect your property long-term.",
  },
];

type ServiceCardViewProps = ServiceCard & {
  onLearnMore: (id: string) => void;
};

const ServiceCardView: React.FC<ServiceCardViewProps> = ({
  id,
  icon,
  title,
  description,
  onLearnMore,
}) => (
  <article className="service-card group bg-surface-container-low border border-outline-variant p-md flex flex-col h-full hover:bg-surface-container-high transition-all duration-300">
    <div className="service-icon w-12 h-12 rounded-full bg-secondary-container flex items-center justify-center mb-md text-secondary">
      <span
        className="material-symbols-outlined"
        style={{ fontVariationSettings: "'FILL' 1" }}
      >
        {icon}
      </span>
    </div>
    <h3 className="font-headline-md text-headline-md text-primary mb-sm">
      {title}
    </h3>
    <p className="font-body-md text-body-md text-on-surface-variant mb-lg flex-grow">
      {description}
    </p>
    <button
      onClick={() => onLearnMore(id)}
      className="inline-flex items-center gap-xs font-label-md text-label-md text-secondary group-hover:underline cursor-pointer text-left"
    >
      Learn More{" "}
      <span className="material-symbols-outlined text-[18px]">
        arrow_forward
      </span>
    </button>
  </article>
);

const serviceDetails: Record<string, ServiceDetail> = {
  residential: {
    category: "Home Care",
    title: "Residential Cleaning",
    description:
      "Our comprehensive residential cleaning service is designed to give you peace of mind and a healthier living environment. We don't just sweep floors; we revitalize your sanctuary. Every room undergoes a rigorous checklist to ensure no corner is left untouched, using only hospital-grade, eco-friendly cleaning agents that are safe for pets and children.",
    benefits: [
      "Deep cleaning packages for a full-home reset",
      "Carpet deep cleaning packages for high-traffic floors",
      "Couch and mattress cleaning for fresher upholstery",
      "Tailored cleaning plans for your specific lifestyle",
      "Allergen-reduction techniques for better air quality",
      "High-touch surface disinfection as standard",
      "Eco-friendly, non-toxic cleaning supplies provided",
      "Fully vetted and background-checked professional cleaners",
    ],
  },
  commercial: {
    category: "Facility Management",
    title: "Commercial Cleaning",
    description:
      "Maintain a professional image and ensure the safety of your clients and employees with our high-standard commercial cleaning. We understand the complexities of larger spaces like warehouses and retail stores, where high traffic demands durable and effective hygiene solutions. Our team works around your operational hours to minimize disruption.",
    benefits: [
      "Compliance with OHS and hygiene regulations",
      "Scalable solutions for small retail to large warehouses",
      "After-hours or early-morning cleaning shifts",
      "Advanced industrial-grade equipment for tough surfaces",
      "Regular quality audits and management reporting",
    ],
  },
  office: {
    category: "Professional Workspace",
    title: "Office Cleaning",
    description:
      "A clean office is a productive office. Our specialized office cleaning services focus on the unique needs of the modern workplace, from sanitizing keyboards and workstations to maintaining professional boardroom aesthetics. We prioritize high-traffic areas like breakrooms and restrooms to prevent the spread of workplace illnesses.",
    benefits: [
      "Precision cleaning of electronic equipment and workstations",
      "Sanitization of common areas and kitchenettes",
      "Discreet and professional staff behavior",
      "Customizable frequency (daily, weekly, or bi-weekly)",
      "Supply management services (restocking soaps/papers)",
    ],
  },
  deep: {
    category: "Intensive Restoration",
    title: "Deep Cleaning",
    description:
      "When regular maintenance isn't enough, our Deep Cleaning service steps in to restore your property to its original glory. This intensive process targets hidden dust, grime buildup behind appliances, and long-neglected areas. Ideal for spring cleaning or pre-event preparations.",
    benefits: [
      "Steam cleaning for upholstered surfaces and carpets",
      "Deep degreasing of kitchen appliances and tiles",
      "Restoration of grout lines and baseboards",
      "Dust removal from vents and high-level fixtures",
      "Detail-oriented scrubbing of all internal windows",
    ],
  },
  construction: {
    category: "Final Touch",
    title: "Post Construction Cleaning",
    description:
      "Transition from building site to move-in ready with our expert post-construction cleaning. We handle the heavy lifting of removing fine construction dust, paint spatters, and adhesive residues. We ensure that your investment looks its absolute best for the final handover or grand opening.",
    benefits: [
      "HEPA-filter vacuuming to capture microscopic dust",
      "Removal of construction debris and materials",
      "Detailed cleaning of cabinets, closets, and drawers",
      "Professional window and frame scraping/cleaning",
      "Pressure washing of external walkways if required",
    ],
  },
  pest: {
    category: "Property Protection",
    title: "Pest Control",
    description:
      "Protect your health and property value with our integrated pest management solutions. Our licensed technicians use the latest scientific approaches to identify, eliminate, and prevent pest infestations. We focus on long-term exclusion strategies rather than just temporary surface treatments.",
    benefits: [
      "Comprehensive termite barrier and baiting systems",
      "Environmentally conscious rodent exclusion methods",
      "Preventative spraying for crawling and flying insects",
      "Safe treatment protocols for food-handling areas",
      "Regular monitoring and preventative maintenance reports",
    ],
  },
};

export const ServicesPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const whatsappLink = "https://wa.me/27679376048";

  const handleLearnMore = (serviceId: string) => {
    setSelectedService(serviceId);
    setModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    document.body.style.overflow = "";
    setSelectedService(null);
  };

  const selectedDetail =
    selectedService && serviceDetails[selectedService]
      ? serviceDetails[selectedService]
      : null;

  return (
    <div className="bg-surface text-on-surface font-body-md selection:bg-secondary-container selection:text-on-secondary-container">
      <header className="relative bg-surface py-lg md:py-xl border-b border-outline-variant overflow-hidden">
        <div className="max-w-container-max mx-auto px-lg relative z-10">
          <div className="max-w-2xl">
            <span className="text-secondary font-label-md tracking-widest uppercase mb-sm block">
              Our Expertise
            </span>
            <h1 className="font-display-lg text-display-lg text-primary mb-md">
              Professional Cleaning Solutions & Pest Control for Every Space
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant mb-lg">
              From residential sanctuaries to high-traffic commercial hubs,
              Kolomaka Cleaning delivers clinical standards of hygiene and
              meticulous attention to detail.
            </p>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
          <span className="material-symbols-outlined text-[300px] absolute -right-20 -top-20 text-primary">
            cleaning_services
          </span>
        </div>
      </header>

      <main className="w-full">
        <section className="max-w-container-max mx-auto px-lg py-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-md">
            {services.map((service) => (
              <ServiceCardView
                key={service.id}
                {...service}
                onLearnMore={handleLearnMore}
              />
            ))}
          </div>
        </section>

        <section className="bg-surface-container-low py-xl">
          <div className="max-w-container-max mx-auto px-lg">
            <div className="flex flex-col md:flex-row items-center gap-lg">
              <div className="w-full md:w-1/2">
                <div className="aspect-video bg-surface-container-highest rounded-xl overflow-hidden shadow-sm relative">
                  <img
                    alt="Pest control service"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuCbH_YuP4MjZXwCgtBTqTh7kLKaSjDUYulbJMtivFDJ3NQ8MDZP09nERdTK4nFvzJB6ScHNUoLrQ4a_12ptYfj_LyKIWNtBxkfAMT4LpP-bD5cKR0kq5avHYHYFPQ9y-6sQEXJsMVx_i3uLvG_1vA3Ojk8rBiw8WStGI29-1w1224H6cRb2DQWkgghelS9uHU2xf59lPseEPOY0TR2nS-vA2Nuzq9fweMgs1DQlp7DahB8B9H9g4HLJUllykCYp2q9OF0YAFt54gEI-"
                  />
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <h2 className="font-headline-lg text-headline-lg text-primary mb-md">
                  Specialized Pest Management
                </h2>
                <p className="font-body-lg text-body-lg text-on-surface-variant mb-md">
                  Our integrated pest management (IPM) approach ensures that we
                  do not just treat the symptoms, but eliminate the source of
                  infestation.
                </p>
                <ul className="space-y-sm mb-lg">
                  <li className="flex items-center gap-sm">
                    <span className="material-symbols-outlined text-secondary">
                      check_circle
                    </span>
                    <span className="font-body-md text-body-md">
                      Termite Protection & Structural Treatment
                    </span>
                  </li>
                  <li className="flex items-center gap-sm">
                    <span className="material-symbols-outlined text-secondary">
                      check_circle
                    </span>
                    <span className="font-body-md text-body-md">
                      Rodent Exclusion & Baiting Systems
                    </span>
                  </li>
                  <li className="flex items-center gap-sm">
                    <span className="material-symbols-outlined text-secondary">
                      check_circle
                    </span>
                    <span className="font-body-md text-body-md">
                      General Disinfection & Crawling Insect Control
                    </span>
                  </li>
                </ul>
                <a
                  className="inline-flex items-center justify-center border-2 border-secondary text-secondary px-lg py-sm rounded-lg font-label-md hover:bg-secondary hover:text-on-primary transition-all"
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  View Detailed Pest Solutions
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-brand-gradient py-xl">
          <div className="max-w-container-max mx-auto px-lg text-center">
            <h2 className="font-display-lg text-display-lg text-on-primary mb-md">
              Ready for a Cleaner Space?
            </h2>
            <p className="font-body-lg text-body-lg text-on-primary-container mb-lg max-w-2xl mx-auto">
              Contact us today for a free inspection or a customized quote
              tailored to your specific facility needs.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-md">
              <a
                className="inline-flex items-center justify-center bg-white text-primary px-lg py-md font-bold rounded-lg hover:brightness-90 transition-all"
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
              >
                Request Free Quote
              </a>
              <a
                className="inline-flex items-center justify-center border border-white text-white px-lg py-md font-bold hover:bg-white/10 transition-all rounded-lg"
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
              >
                Schedule Inspection
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Service Detail Modal */}
      {modalOpen && selectedDetail && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-md"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-outline-variant p-lg flex justify-between items-center">
              <h2 className="font-headline-lg text-headline-lg text-primary">
                {selectedDetail.title}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-on-surface-variant hover:text-primary transition-colors"
              >
                <span className="material-symbols-outlined text-2xl">
                  close
                </span>
              </button>
            </div>

            <div className="p-lg space-y-lg">
              <div>
                <h3 className="font-headline-md text-headline-md text-secondary mb-sm">
                  {selectedDetail.category}
                </h3>
                <p className="font-body-lg text-body-lg text-on-surface-variant">
                  {selectedDetail.description}
                </p>
              </div>

              <div>
                <h4 className="font-headline-md text-headline-md text-primary mb-md">
                  Key Benefits:
                </h4>
                <ul className="space-y-sm">
                  {selectedDetail.benefits.map((benefit) => (
                    <li
                      key={benefit}
                      className="flex items-start gap-sm text-body-md text-on-surface-variant"
                    >
                      <span className="material-symbols-outlined text-secondary flex-shrink-0 mt-0.5">
                        check_circle
                      </span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex gap-md pt-md border-t border-outline-variant">
                <a
                  className="flex-1 bg-brand-gradient text-on-primary px-lg py-md font-bold rounded-lg hover:brightness-110 transition-all text-center"
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Get a Quote
                </a>
                <button
                  onClick={handleCloseModal}
                  className="flex-1 border border-outline-variant text-on-surface-variant px-lg py-md font-bold rounded-lg hover:bg-surface-container transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
